import React, { useMemo, useState, useCallback } from "react";
import { useUnit } from "effector-react";
import "../Complexes/ResidentialComplexList.css";
import IncidentBadge from "../Utils/IncidentBadge";

import { IconButton, Tooltip, CircularProgress } from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ConfirmDialog from "../ConfirmModal";

import { $canDeleteComplex, $canEditComplex } from "../../store/auth";
import { $deletingCorpsIds, deleteCorpsFx } from "../../store/buildingCorps";
import EditCorpsDialog from "./EditCorpsDialog";

// иконки — как в остальных карточках
import icLeak from "../../assets/icons/incidents/ic-inc-leak.svg";
import icBatteryLow from "../../assets/icons/incidents/ic-inc-battery-low.svg";
import icTamper from "../../assets/icons/incidents/ic-inc-tamper.svg";
import icInspection from "../../assets/icons/incidents/ic-inc-inspection.svg";
import icBreak from "../../assets/icons/incidents/ic-inc-break.svg";
import icScreenBlock from "../../assets/icons/incidents/ic-inc-screen-block.svg";
import icNoReadings from "../../assets/icons/incidents/ic-inc-no-readings.svg";

const ICONS = {
  leak: icLeak,
  lowBattery: icBatteryLow,
  tamper: icTamper,
  inspection: icInspection,
  break: icBreak,
  screenBlock: icScreenBlock,
  noReadings: icNoReadings,
};

function stopAll(e) {
  e.stopPropagation?.();
}

export default function CorpsRow({
  id,
  name,
  createdAt,
  buildingId, // опционально показать в средней колонке
  incidents = {}, // если появятся реальные инциденты - прокинете сюда
  onClick,
  isNew = false,
  devices = {
    cold: 0,
    hot: 0,
    heat: 0,
    heatAllocators: 0,
    electricity: 0,
    total: 0,
  },
  onEdited,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [forceConfirmOpen, setForceConfirmOpen] = useState(false);
  const [deviceCount, setDeviceCount] = useState(null);
  const [deletePending, setDeletePending] = useState(false);
  const [canDelete, canEdit, deletingIds, deleteCorps] = useUnit([
    $canDeleteComplex,
    $canEditComplex,
    $deletingCorpsIds,
    deleteCorpsFx,
  ]);
  const deleting = deletingIds.has(String(id));
  const [editOpen, setEditOpen] = useState(false);

  const data = useMemo(
    () => ({
      leak: incidents.leak ?? 0,
      lowBattery: incidents.lowBattery ?? 0,
      tamper: incidents.tamper ?? 0,
      inspection: incidents.inspection ?? 0,
      break: incidents.break ?? 0,
      screenBlock: incidents.screenBlock ?? 0,
      noReadings: incidents.noReadings ?? 0,
    }),
    [incidents],
  );

  const handleDelete = useCallback(
    async (force) => {
      if (!canDelete) return;
      setDeletePending(true);
      try {
        await deleteCorps({ id: String(id), force });
      } catch (err) {
        const status = err?.response?.status ?? err?.status;
        const detail = err?.response?.data?.detail ?? err?.data?.detail;
        const count = detail?.device_count ?? err?.response?.data?.device_count;
        if (
          status === 409 &&
          typeof count === "number" &&
          detail?.action === "confirm_corps_delete"
        ) {
          setDeviceCount(count);
          setForceConfirmOpen(true);
          return;
        }
        console.error("delete corps failed", err);
      } finally {
        setDeletePending(false);
      }
    },
    [canDelete, deleteCorps, id],
  );

  const handleCardClick = (e) => {
    if (!onClick) return;
    if (editOpen || confirmOpen || forceConfirmOpen) {
      e?.preventDefault?.();
      return;
    }
    if (e?.defaultPrevented) return;
    if (
      e?.target instanceof Element &&
      e.target.closest('[data-interactive="true"]')
    ) {
      return;
    }
    onClick();
  };

  const handleKey = (e) => {
    if (!onClick || editOpen || confirmOpen || forceConfirmOpen) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`rc-building-row ${onClick ? "is-clickable" : ""} ${
        isNew ? "is-new" : ""
      } ${deleting ? "rc-card--deleting" : ""}`}
      onClick={handleCardClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={handleKey}
      style={{ position: "relative" }}
    >
      {canEdit && (
        <Tooltip title="Редактировать корпус" style={{ zIndex: 2 }}>
          <span
            style={{
              position: "absolute",
              top: 8,
              right: canDelete ? 44 : 8,
              zIndex: 2,
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onTouchStart={stopAll}
            data-interactive="true"
          >
            <IconButton
              size="small"
              color="primary"
              aria-label="Редактировать корпус"
              onClick={(e) => {
                stopAll(e);
                setEditOpen(true);
              }}
              onMouseDown={stopAll}
              onTouchStart={stopAll}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") stopAll(e);
              }}
              sx={{
                bgcolor: (t) =>
                  t.palette.mode === "light"
                    ? "rgba(0,0,0,0.04)"
                    : "rgba(255,255,255,0.06)",
                "&:hover": {
                  bgcolor: (t) =>
                    t.palette.mode === "light"
                      ? "rgba(0,0,0,0.08)"
                      : "rgba(255,255,255,0.12)",
                },
              }}
              data-interactive="true"
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {/* Кнопка удаления */}
      {canDelete && (
        <Tooltip title="Удалить корпус" style={{ zIndex: 2 }}>
          <span
            style={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onTouchStart={stopAll}
            data-interactive="true"
          >
            <IconButton
              size="small"
              color="error"
              disabled={deleting || deletePending}
              aria-label="Удалить корпус"
              onClick={(e) => {
                stopAll(e);
                const total = Number(devices?.total || 0);
                if (total > 0) {
                  setDeviceCount(total);
                  setForceConfirmOpen(true);
                } else {
                  setConfirmOpen(true);
                }
              }}
              onMouseDown={stopAll}
              onTouchStart={stopAll}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") stopAll(e);
              }}
              sx={{
                bgcolor: (t) =>
                  t.palette.mode === "light"
                    ? "rgba(0,0,0,0.04)"
                    : "rgba(255,255,255,0.06)",
                "&:hover": {
                  bgcolor: (t) =>
                    t.palette.mode === "light"
                      ? "rgba(0,0,0,0.08)"
                      : "rgba(255,255,255,0.12)",
                },
              }}
              data-interactive="true"
            >
              {deleting || deletePending ? (
                <CircularProgress size={18} />
              ) : (
                <DeleteOutlineRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Левая колонка — как у зданий: название */}
      <div className="rc-brow-col rc-brow-col--addr">
        <div className="rc-brow-title">Корпус</div>
        <div className="rc-brow-name" title={name || ""}>
          {name || "—"}
        </div>

        {/* Можно показать ID корпуса мелко, если нужно */}
        {/* <div className="rc-brow-subtitle">ID</div>
        <div className="rc-brow-addr" title={id}>{id}</div> */}
      </div>

      {/* Средняя колонка — Информация (аналог «счётчиков») */}
      <div className="rc-brow-col rc-brow-col--meters">
        <div className="rc-brow-title">Количество устройств</div>
        <div className="rc-meters rc-meters--stack rc-meters--tight">
          {/* Вариант 1: общий набор */}
          {/* <div className="rc-meter">
            <div className="rc-meter__type">Счётчики</div>
            <div className="rc-meter__value">{devices.meters ?? (devices.cold ?? 0) + (devices.hot ?? 0) + (devices.heat ?? 0)}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">Датчики</div>
            <div className="rc-meter__value">{devices.sensors ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">Шлюзы</div>
            <div className="rc-meter__value">{devices.gateways ?? 0}</div>
          </div> */}
          {/* Вариант 2 (если хотите разложить именно по типам воды/тепла): */}
          <div className="rc-meter">
            <div className="rc-meter__type">ХВС</div>
            <div className="rc-meter__value">{devices.cold ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">ГВС</div>
            <div className="rc-meter__value">{devices.hot ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">Отопление</div>
            <div className="rc-meter__value">{devices.heat ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">УРТ</div>
            <div className="rc-meter__value">{devices.heatAllocators ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">Электросчетчики</div>
            <div className="rc-meter__value">{devices.electricity ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">Всего</div>
            <div className="rc-meter__value">
              {devices.total ??
                (devices.cold ?? 0) +
                  (devices.hot ?? 0) +
                  (devices.heat ?? 0) +
                  (devices.heatAllocators ?? 0) +
                  (devices.electricity ?? 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Правая колонка — Инциденты (как в карточках) */}
      <div className="rc-brow-col rc-brow-col--inc">
        <div className="rc-brow-title">Инциденты</div>

        <div className="rc-incidents rc-incidents--two rc-incidents--compact">
          <div className="rc-inc-col">
            {[
              { key: "leak", label: "Протечки", alertIf: (n) => n > 0 },
              { key: "lowBattery", label: "Низкий заряд" },
              { key: "tamper", label: "Воздействие магнитом" },
              { key: "inspection", label: "Поверка" },
              { key: "break", label: "Прорыв" },
            ].map(({ key, label, alertIf }) => (
              <div key={key} className="rc-inc-row">
                <span className="rc-inc-label">{label}</span>
                <IncidentBadge
                  icon={ICONS[key]}
                  count={data[key]}
                  alert={alertIf ? alertIf(data[key]) : false}
                  title={label}
                />
              </div>
            ))}
          </div>

          <div className="rc-inc-col rc-inc-col_right">
            {[
              { key: "screenBlock", label: "Блок. экрана" },
              {
                key: "noReadings",
                label: "Отсутствие показаний",
                alertIf: (n) => n > 0,
              },
            ].map(({ key, label, alertIf }) => (
              <div key={key} className="rc-inc-row">
                <span className="rc-inc-label">{label}</span>
                <IncidentBadge
                  icon={ICONS[key]}
                  count={data[key]}
                  alert={alertIf ? alertIf(data[key]) : false}
                  title={label}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <EditCorpsDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        corps={{ id, name, buildingId }}
        onSaved={async () => {
          setEditOpen(false);
          await onEdited?.(id);
        }}
      />
      <ConfirmDialog
        open={confirmOpen}
        title="Удаление корпуса"
        description={`Удалить корпус «${name || id}»? Это действие необратимо.`}
        confirmText="Удалить"
        cancelText="Отмена"
        confirmColor="error"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          setConfirmOpen(false);
          await handleDelete(false);
        }}
      />
      <ConfirmDialog
        open={forceConfirmOpen}
        title="Удалить корпус со всеми данными?"
        description={`В корпусе находится ${deviceCount ?? 0} приборов. Будут удалены квартиры этого корпуса, приборы, показания и история. Дом и другие корпуса останутся без изменений. Восстановить данные будет нельзя.`}
        confirmText="Удалить корпус"
        cancelText="Отмена"
        confirmColor="warning"
        onCancel={() => setForceConfirmOpen(false)}
        onConfirm={async () => {
          setForceConfirmOpen(false);
          await handleDelete(true);
        }}
      />
    </div>
  );
}
